import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI, proveedoresAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Bird, CalendarBlank, CurrencyDollar, Hash, ArrowsLeftRight, Warehouse, FolderOpen, ArrowLeft, ShieldCheck, PencilSimple } from '@phosphor-icons/react';

// Componente Input con formato de miles
const FormattedNumberInput = ({ value, onChange, placeholder, className, id, name, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value?.toString() || '');

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value?.toString() || '');
    }
  }, [value, isFocused]);

  const formatWithCommas = (num) => {
    if (!num && num !== 0) return '';
    return Number(num).toLocaleString('en-US');
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const numValue = parseInt(localValue.replace(/,/g, '')) || 0;
    onChange({ target: { name, value: numValue.toString() } });
  };

  const handleChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setLocalValue(val);
  };

  return (
    <Input
      id={id}
      name={name}
      type="text"
      className={className}
      value={isFocused ? localValue : formatWithCommas(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      {...props}
    />
  );
};

export default function LoteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(true);
  const [originalLote, setOriginalLote] = useState(null);
  
  // Modal de código super usuario
  const [showSuperUserModal, setShowSuperUserModal] = useState(false);
  const [superUserCode, setSuperUserCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    nave: '',
    proyecto: '',
    cantidad_pollitos: '',
    precio_pollito: '',
    fecha_inicio: '',
    tasa_cambio: '61.5',
    moneda_pollitos: 'USD',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [loteRes, proveedoresRes] = await Promise.all([
        lotesAPI.getOne(id),
        proveedoresAPI.getAll()
      ]);
      
      const lote = loteRes.data;
      setOriginalLote(lote);
      setProveedores(proveedoresRes.data || []);
      
      // Poblar el formulario con los datos del lote
      setFormData({
        nombre: lote.nombre || '',
        nave: lote.nave || '',
        proyecto: lote.proyecto || '',
        cantidad_pollitos: lote.cantidad_pollitos?.toString() || '',
        precio_pollito: lote.precio_pollito?.toString() || '',
        fecha_inicio: lote.fecha_inicio ? lote.fecha_inicio.split('T')[0] : '',
        tasa_cambio: lote.tasa_cambio?.toString() || '61.5',
        moneda_pollitos: lote.moneda_pollitos || 'USD',
      });
    } catch (error) {
      toast.error('Error al cargar el lote');
      navigate('/lotes');
    } finally {
      setLoading(false);
      setLoadingProveedores(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.cantidad_pollitos || !formData.precio_pollito) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }
    
    // Mostrar modal para código de super usuario
    setShowSuperUserModal(true);
  };

  const handleConfirmSave = async () => {
    if (!superUserCode) {
      toast.error('Debe ingresar el código de super usuario');
      return;
    }

    setValidatingCode(true);
    setSaving(true);
    
    try {
      const payload = {
        nombre: formData.nombre,
        nave: formData.nave || null,
        proyecto: formData.proyecto || null,
        cantidad_pollitos: parseInt(formData.cantidad_pollitos),
        precio_pollito: parseFloat(formData.precio_pollito),
        tasa_cambio: parseFloat(formData.tasa_cambio) || 61.5,
        moneda_pollitos: formData.moneda_pollitos,
      };
      
      // Enviar actualización con código de super usuario
      await lotesAPI.update(id, payload, superUserCode);
      
      toast.success('Lote actualizado exitosamente');
      setShowSuperUserModal(false);
      navigate(`/lotes/${id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al actualizar el lote';
      if (message.includes('código') || message.includes('super')) {
        toast.error('Código de super usuario incorrecto');
      } else {
        toast.error(message);
      }
    } finally {
      setValidatingCode(false);
      setSaving(false);
    }
  };

  const tasa = parseFloat(formData.tasa_cambio) || 61.5;
  const precioNum = parseFloat(formData.precio_pollito) || 0;
  const cantidadNum = parseInt(formData.cantidad_pollitos) || 0;
  
  const costoOriginal = cantidadNum * precioNum;
  const costoDOP = formData.moneda_pollitos === 'USD' ? costoOriginal * tasa : costoOriginal;
  const costoUSD = formData.moneda_pollitos === 'USD' ? costoOriginal : costoOriginal / tasa;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64 mb-6" />
          <Card>
            <CardContent className="pt-6 space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto animate-fade-in" data-testid="edit-lote-form">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/lotes/${id}`)}
            className="mb-2"
          >
            <ArrowLeft size={16} className="mr-1" />
            Volver al Lote
          </Button>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Editar Lote
          </h1>
          <p className="text-muted-foreground">
            Modifique los datos del lote: <strong>{originalLote?.nombre}</strong>
          </p>
        </div>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PencilSimple size={24} className="text-[#F69031]" weight="fill" />
              Información del Lote
            </CardTitle>
            <CardDescription>
              Actualice los datos necesarios del lote
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitClick} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre del Lote *</Label>
                <Input
                  id="nombre"
                  name="nombre"
                  placeholder="Ej: Lote 1, Enero 2024"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="input-focus"
                  data-testid="edit-lote-nombre"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nave" className="flex items-center gap-2">
                    <Warehouse size={16} />
                    Nave #
                  </Label>
                  <Input
                    id="nave"
                    name="nave"
                    placeholder="Ej: 1, A, Norte"
                    value={formData.nave}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="edit-lote-nave"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="proyecto" className="flex items-center gap-2">
                    <FolderOpen size={16} />
                    Proyecto
                  </Label>
                  <Input
                    id="proyecto"
                    name="proyecto"
                    placeholder="Ej: Proyecto Principal"
                    value={formData.proyecto}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="edit-lote-proyecto"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cantidad_pollitos" className="flex items-center gap-2">
                    <Hash size={16} />
                    Cantidad de Pollitos *
                  </Label>
                  <FormattedNumberInput
                    id="cantidad_pollitos"
                    name="cantidad_pollitos"
                    placeholder="Ej: 20,000"
                    value={formData.cantidad_pollitos}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="edit-lote-cantidad"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="precio_pollito" className="flex items-center gap-2">
                    <CurrencyDollar size={16} />
                    Precio por Pollito ($) *
                  </Label>
                  <Input
                    id="precio_pollito"
                    name="precio_pollito"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Ej: 0.50"
                    value={formData.precio_pollito}
                    onChange={handleChange}
                    className="input-focus"
                    data-testid="edit-lote-precio"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fecha_inicio" className="flex items-center gap-2">
                  <CalendarBlank size={16} />
                  Fecha de Inicio
                </Label>
                <Input
                  id="fecha_inicio"
                  name="fecha_inicio"
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={handleChange}
                  className="input-focus"
                  data-testid="edit-lote-fecha"
                  disabled
                />
                <p className="text-xs text-muted-foreground">La fecha de inicio no se puede modificar</p>
              </div>

              {/* Configuración de Moneda */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <ArrowsLeftRight size={18} className="text-[#F69031]" />
                  Configuración de Moneda
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tasa_cambio">Tasa de Cambio (USD → DOP)</Label>
                    <Input
                      id="tasa_cambio"
                      name="tasa_cambio"
                      type="number"
                      step="0.01"
                      min="1"
                      placeholder="61.50"
                      value={formData.tasa_cambio}
                      onChange={handleChange}
                      className="input-focus"
                      data-testid="edit-lote-tasa"
                    />
                    <p className="text-xs text-muted-foreground">1 USD = {formData.tasa_cambio} DOP</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="moneda_pollitos">Moneda del Precio de Pollitos</Label>
                    <Select
                      value={formData.moneda_pollitos}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, moneda_pollitos: value }))}
                    >
                      <SelectTrigger data-testid="edit-lote-moneda">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD (Dólar)</SelectItem>
                        <SelectItem value="DOP">DOP (Peso Dominicano)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Cost Preview */}
              <div className="bg-muted/50 rounded-lg p-4 border border-border/60">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Costo Inicial de Pollitos</p>
                    <p className="text-xs text-muted-foreground">
                      {cantidadNum.toLocaleString()} pollitos × {formData.moneda_pollitos === 'USD' ? '$' : 'RD$'}{precioNum.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#E73425]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      RD$ {costoDOP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ≈ US$ {costoUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/lotes/${id}`)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#F69031] hover:bg-[#E58020] btn-animate"
                  disabled={saving}
                  data-testid="submit-edit-lote"
                >
                  {saving ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    <>
                      <ShieldCheck size={18} className="mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-border/60 bg-amber-50/50">
          <CardContent className="pt-6">
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <ShieldCheck size={18} className="text-amber-600" />
              Edición Protegida
            </h4>
            <p className="text-sm text-muted-foreground">
              Para guardar los cambios se requiere el código de super usuario. Esto protege la integridad de los datos del lote.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Modal de Super Usuario */}
      <Dialog open={showSuperUserModal} onOpenChange={setShowSuperUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-amber-500" />
              Confirmar Edición
            </DialogTitle>
            <DialogDescription>
              Ingrese el código de super usuario para guardar los cambios en el lote.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="superuser-code">Código de Super Usuario</Label>
              <Input
                id="superuser-code"
                type="password"
                placeholder="Ingrese el código"
                value={superUserCode}
                onChange={(e) => setSuperUserCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirmSave()}
                data-testid="superuser-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSuperUserModal(false);
                setSuperUserCode('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={validatingCode || !superUserCode}
              className="bg-[#F69031] hover:bg-[#E58020]"
            >
              {validatingCode ? (
                <div className="spinner h-4 w-4 mr-2" />
              ) : (
                <ShieldCheck size={16} className="mr-2" />
              )}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
